import React, { ReactElement, useMemo } from 'react'
import { Form } from 'react-bootstrap'
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaAngleLeft,
  FaAngleRight,
  FaCaretDown,
  FaCaretUp,
} from 'react-icons/fa'
import {
  Column,
  ColumnInstance,
  HeaderGroup,
  Row,
  useFilters,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import { translate } from '../../localization/localizationUtils'
import './BnfTable.scss'
import IBnfTableDefColumn from './IBnfTableDefColumn'
import { ZipCelXConfig, ZipCelXCell } from 'zipcelx'
import generateExcel from 'zipcelx'

type DefaultSortByCol = {
  id: string
  desc?: boolean
}

type Props = {
  source: Array<any>
  columnsDef: Array<IBnfTableDefColumn>
  defaultSort?: Array<DefaultSortByCol>
  downloadableExcel?: boolean
  customCreateExcelConfig?: (
    headerGroups: HeaderGroup<object>[],
    rows: Row<object>[],
    excelName: string
  ) => ZipCelXConfig
  excelName?: string
}

type FilterProps = {
  column: ColumnInstance
}

// Define a default UI for filtering
const DefaultColumnFilter = ({ column }: FilterProps) => {
  return (
    <Form.Control
      className="filter"
      type="search"
      placeholder=""
      value={column.filterValue || ''}
      onChange={(e) => {
        column.setFilter(e.target?.value || undefined) // Set undefined to remove the filter entirely
      }}
      style={{
        width: '100%',
        maxWidth: column.maxWidth ? `${column.maxWidth}px` : 'auto',
      }}
    />
  )
}

const SelectColumnFilter = ({ column }: FilterProps) => {
  const options = useMemo<string[]>(() => {
    const options = new Set<string>()
    column.preFilteredRows?.forEach((row) => {
      options.add(row.values[column.id])
    })
    return [...options.values()]
  }, [column.id, column.preFilteredRows])

  return (
    <Form.Select
      className="filter"
      value={column.filterValue}
      onChange={(e) => column.setFilter(e.target?.value || undefined)}
      style={{
        width: '100%',
        maxWidth: column.maxWidth ? `${column.maxWidth}px` : 'auto',
      }}
    >
      <option value="">{translate('filter-All')}</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </Form.Select>
  )
}

const defaultSortFunction = (
  rowA: Row,
  rowB: Row,
  columnId: string,
  desc: boolean
) => {
  const cellA = rowA.values[columnId]?.toLowerCase()
  const cellB = rowB.values[columnId]?.toLowerCase()

  if (cellA > cellB) return 1
  if (cellA < cellB) return -1
  return 0
}

const defaultNumbersSortFunction = (
  rowA: Row,
  rowB: Row,
  columnId: string,
  desc: boolean
) => {
  const cellA = Number(rowA.values[columnId])
  const cellB = Number(rowB.values[columnId])

  if (cellA > cellB) return 1
  if (cellA < cellB) return -1
  return 0
}

const defaultValueFormat = ({ cell: { value } }: any) => {
  return <>{value}</>
}

function getHeader(column: HeaderGroup<object>): ZipCelXCell[] {
  if (column.totalHeaderCount === 1 || !column.totalHeaderCount) {
    return [
      {
        value:
          (column.Header as ReactElement).props?.children?.toString() || '',
        type: 'string',
      },
    ]
  } else {
    const span = [...Array(column.totalHeaderCount - 1)].map((x) => ({
      value: '',
      type: 'string',
    }))
    return [
      {
        value:
          (column.Header as ReactElement).props?.children?.toString() || '',
        type: 'string',
      },
      ...(span as unknown[] as ZipCelXCell[]),
    ]
  }
}

const createExcelConfig = (
  headerGroups: HeaderGroup<object>[],
  rows: Row<object>[],
  name: string
): ZipCelXConfig => {
  const config: ZipCelXConfig = {
    filename: name,
    sheet: {
      data: [],
    },
  }
  const dataSet = config.sheet.data
  const exportedColumns: string[] = []

  headerGroups.forEach((headerGroup) => {
    const headerRow: ZipCelXCell[] = []
    if (headerGroup.headers) {
      headerGroup.headers.forEach((column) => {
        exportedColumns.push(column.id)
        headerRow.push(...(getHeader(column) as unknown[] as ZipCelXCell[]))
      })
    }
    dataSet.push(headerRow)
  })

  if (rows.length > 0) {
    rows.forEach((row) => {
      const dataRow: ZipCelXCell[] = []
      Object.keys(row.values).forEach(
        (id) =>
          exportedColumns.includes(id) &&
          dataRow.push({
            value: row.values[id],
            type: typeof row.values[id] === 'number' ? 'number' : 'string',
          })
      )

      dataSet.push(dataRow)
    })
  }
  return config
}

const BnfTablePaged = ({
  source,
  columnsDef,
  defaultSort,
  downloadableExcel,
  customCreateExcelConfig,
  excelName,
}: Props) => {
  const defaultSortType = useMemo<any>(() => defaultSortFunction, [])
  const defaultNumbersSortType = useMemo<any>(
    () => defaultNumbersSortFunction,
    []
  )
  const projectsData = useMemo<Array<any>>(() => source, [source])
  const columns = useMemo<Array<Column>>(
    () =>
      columnsDef.map((x) => {
        return {
          accessor: x.fieldName,
          Header: (
            <div
              className={`d-inline-flex align-items-center ${
                x.align === 'center'
                  ? 'w-100 justify-content-center'
                  : x.align === 'right'
                  ? 'w-100 justify-content-end'
                  : ''
              }`}
            >
              {translate(x.caption)}
            </div>
          ),
          disableFilters: x.filterType === 'none',
          Filter:
            x.filterType === 'select'
              ? SelectColumnFilter
              : DefaultColumnFilter,
          sortType:
            x.sortType === 'num' ? defaultNumbersSortType : defaultSortType,
          disableSortBy: x.sortType === 'none',
          Cell: (p: any) => (
            <div
              className={`d-flex align-items-center ${
                x.align === 'center'
                  ? 'justify-content-center'
                  : x.align === 'right'
                  ? 'justify-content-end'
                  : ''
              }`}
            >
              {x.valueFormat ? x.valueFormat(p) : defaultValueFormat(p)}
            </div>
          ),
          isVisible: x.isVisible,
          maxWidth: x.maxWidthPx,
        }
      }),
    [defaultSortType, defaultNumbersSortType, columnsDef]
  )

  const defaultColumn = React.useMemo<any>(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  )

  const hiddenCol: string[] = columnsDef
    .filter((x) => !(x.isVisible ?? true))
    .map((x) => x.fieldName)

  const onDownloadExcel = () => {
    const config = customCreateExcelConfig
      ? customCreateExcelConfig(headerGroups, rows, excelName || 'export')
      : createExcelConfig(headerGroups, rows, excelName || 'export')
    generateExcel(config)
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: projectsData,
      defaultColumn,
      initialState: {
        hiddenColumns: hiddenCol,
        pageIndex: 0,
        pageSize: 20,
        sortBy: defaultSort || [],
      },
    },
    useFilters,
    useSortBy,
    usePagination
  )

  return (
    <div id="bnf-table-wrapper" className="h-100 flex-scrollable-container">
      <section className="h-100 overflow-auto">
        <table {...getTableProps()} className="table table-striped">
          {headerGroups.map((group, index) => {
            return (
              <thead key={index}>
                <tr
                  {...group.getHeaderGroupProps()}
                  className="bnf-table-captions"
                >
                  {group.headers.map((column) => {
                    return (
                      <th key={column.id}>
                        <div
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                        >
                          {column.render('Header')}
                          <span>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <FaCaretDown />
                              ) : (
                                <FaCaretUp />
                              )
                            ) : (
                              ''
                            )}
                          </span>
                        </div>
                      </th>
                    )
                  })}
                </tr>
                <tr className="bnf-table-filters">
                  {group.headers.map((column) => {
                    return (
                      <th key={column.id}>
                        {column.canFilter && column.render('Filter')}
                      </th>
                    )
                  })}
                </tr>
              </thead>
            )
          })}
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()} key={row.id}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()} key={cell.column.id}>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
      <section className="pt-3 d-flex justify-content-between align-items-center">
        <div className="pagination d-flex align-items-center">
          <button
            className="btn btn-secondary"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            <FaAngleDoubleLeft />
          </button>{' '}
          <button
            className="btn btn-secondary ms-1"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            <FaAngleLeft />
          </button>{' '}
          <button
            className="btn btn-secondary ms-1"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            <FaAngleRight />
          </button>{' '}
          <button
            className="btn btn-secondary ms-1"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            <FaAngleDoubleRight />
          </button>{' '}
          <span className="ms-3">
            {translate('table-PageXofY', {
              page: (pageIndex + 1).toString(),
              totalPages: pageOptions.length.toString(),
            })}
          </span>
          <span className="ms-3">|</span>
          <span className="ms-3">{translate('table-ShowPage')}: </span>
          <Form.Control
            className="d-inline"
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0
              gotoPage(page)
            }}
            disabled={!canPreviousPage && !canNextPage}
            style={{ width: 'auto', height: 'auto', maxWidth: '50px' }}
          />
          <Form.Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value))
            }}
            className="ms-3"
            style={{ width: 'auto', height: 'auto' }}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {translate('table-Show')} {pageSize}
              </option>
            ))}
          </Form.Select>
        </div>
        {downloadableExcel && (
          <button
            className="btn btn-outline-secondary"
            onClick={onDownloadExcel}
          >
            Export XLSX
          </button>
        )}
      </section>
    </div>
  )
}

export default BnfTablePaged
